import {
  PanelElementType,
  hasRoundHole,
  type ElementMountingHoleConfig,
  type MountingHole,
  type PanelElement,
} from "./panelTypes";

function getElementExtent(element: PanelElement): number | null {
  if (hasRoundHole(element)) {
    return element.properties.diameterMm / 2;
  }
  switch (element.type) {
    case PanelElementType.Switch:
    case PanelElementType.Rectangle:
    case PanelElementType.Slot:
    case PanelElementType.Triangle:
    case PanelElementType.Oval: {
      const props = element.properties as { widthMm: number; heightMm: number };
      return Math.max(props.widthMm, props.heightMm) / 2;
    }
    default:
      return null;
  }
}

export function computeElementMountingHoles(
  elements: PanelElement[],
  config: ElementMountingHoleConfig,
): MountingHole[] {
  if (!config.enabled || config.count <= 0 || config.diameterMm <= 0) {
    return [];
  }

  const holes: MountingHole[] = [];
  const angleStep = (Math.PI * 2) / config.count;
  const holeRadius = config.diameterMm / 2;

  elements.forEach((element) => {
    const elementEnabled =
      typeof element.mountingHolesEnabled === "boolean"
        ? element.mountingHolesEnabled
        : config.enabled;
    if (!elementEnabled) {
      return;
    }
    const extent = getElementExtent(element);
    if (!extent) {
      return;
    }
    const radius = extent + config.offsetMm + holeRadius;
    if (!Number.isFinite(radius) || radius <= 0) {
      return;
    }
    const elementRotation = ((element.rotationDeg ?? 0) * Math.PI) / 180;
    const holeRotation = ((element.mountingHoleRotationDeg ?? config.rotationDeg) * Math.PI) / 180;

    for (let index = 0; index < config.count; index += 1) {
      const angle = holeRotation + elementRotation + index * angleStep;
      holes.push({
        center: {
          x: element.positionMm.x + Math.cos(angle) * radius,
          y: element.positionMm.y + Math.sin(angle) * radius,
        },
        diameterMm: config.diameterMm,
        shape: "circle",
      });
    }
  });

  return holes;
}
